'use client';

import { useSession } from '@shared/auth';
import { getSiteConfig } from '@shared/config';
import { buildPlayerTrackerImpressionPayload, PLAYER_TRACKER_IMPRESSION, usePlayerTrackerServiceEvent } from '@shared/tracking';
import { NumberFormats, ROUTES, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { useGlobalSheet } from '../../../../global-sheets/context/GlobalSheetContext';
import type { EditableNavigationMenuItemProps } from '../../../../NavigationMenu/components/NavigationMenuItem/types';
import { NavigationMenu } from '../../../../NavigationMenu/NavigationMenu';

type BalanceMenuItemsProps = {
  cashBalance?: number;
  bonusBalance?: number;
  lockedFunds?: number;
  isWalletTransactionsLoading: boolean;
  namespace: string;
  className?: string;
};
function BalanceMenuItems({ cashBalance, bonusBalance, lockedFunds, isWalletTransactionsLoading, namespace, className }: BalanceMenuItemsProps) {
  const t = useTranslations(namespace);
  const { session } = useSession();
  const playerId = session.profile?.id;
  const ptsTrackEvent = usePlayerTrackerServiceEvent();
  const hasTrackedLockedFundsImpressionRef = useRef(false);
  const isLockedFundsShown = Boolean(lockedFunds && lockedFunds > 0);
  const formatCurrency = useCurrencyFormatter();
  const router = useRouter();
  const { openWithdrawSheet } = useGlobalSheet();
  const { showsLockedFundsDetailsCta } = getSiteConfig().MY_ACCOUNT_SETTINGS;
  const withdrawDescription = t('withdraw_description');

  const balanceMenuItems: EditableNavigationMenuItemProps[] = [
    {
      label: t('cash'),
      data: formatCurrency(cashBalance || 0)
        .format(NumberFormats.WITH_DECIMALS)
        .get(),
      variant: 'editable',
      buttonText: t('withdraw_button'),
      chevron: false,
      hierarchy: 'high',
      'data-testid': 'wallet-cash-menu-item',
      'button-data-testid': 'wallet-cash-menu-item-button',
      onClick: () => {
        openWithdrawSheet();
      },
      isLoading: isWalletTransactionsLoading,
      'data-track-container': 'Withdrawable',
      'data-track-name': 'Withdrawable',
      buttonTrackName: 'Withdraw',
      description: withdrawDescription || undefined,
    },
    {
      label: t('bonus'),
      data: formatCurrency(bonusBalance || 0)
        .format(NumberFormats.WITH_DECIMALS)
        .get(),
      variant: 'editable',
      buttonText: t('view_bonus_button'),
      buttonVariant: 'ghost',
      chevron: false,
      hierarchy: 'high',
      'data-testid': 'wallet-bonus-menu-item',
      'button-data-testid': 'wallet-bonus-menu-item-button',
      onClick: () => {
        router.push(ROUTES.OFFERS_BONUSES);
      },
      isLoading: isWalletTransactionsLoading,
      'data-track-container': 'Bonus',
      'data-track-name': 'Bonus',
      buttonTrackName: 'View Bonus',
    },
  ];

  const lockedFundsMenuItem: EditableNavigationMenuItemProps = {
    label: t('locked_funds'),
    data: formatCurrency(lockedFunds || 0)
      .format(NumberFormats.WITH_DECIMALS)
      .get(),
    variant: 'editable',
    description: t('locked_funds_description'),
    chevron: false,
    hierarchy: 'high',
    isLoading: isWalletTransactionsLoading,
    'data-testid': 'wallet-locked-funds-menu-item',
    'data-track-container': 'Locked Funds',
    'data-track-name': 'Locked Funds',
    ...(showsLockedFundsDetailsCta
      ? {
          buttonText: t('view_locked_funds_button'),
          buttonVariant: 'ghost' as const,
          buttonTrackName: 'View Details',
          'button-data-testid': 'wallet-locked-funds-menu-item-button',
          onClick: () => router.push(ROUTES.MY_ACCOUNT.LOCKED_FUNDS),
        }
      : {}),
  };

  if (lockedFunds && lockedFunds > 0) {
    balanceMenuItems.push(lockedFundsMenuItem);
  }

  useEffect(() => {
    if (!isLockedFundsShown || !playerId || hasTrackedLockedFundsImpressionRef.current) return;
    hasTrackedLockedFundsImpressionRef.current = true;
    ptsTrackEvent(
      buildPlayerTrackerImpressionPayload(PLAYER_TRACKER_IMPRESSION.BIG_WIN_STAKE_BLOCK, {
        id: playerId,
      })
    );
  }, [isLockedFundsShown, playerId, ptsTrackEvent]);

  return <NavigationMenu variant="secondary" className={className} items={balanceMenuItems} data-track-section="Balances" />;
}

export { BalanceMenuItems };
